/**
 * Generic keyset-cursor paginator per SPEC-CONVENTIONS:
 *
 *   request  `?cursor=<opaque base64>&limit=<n<=200>`
 *   response `{ items, next_cursor }`, `next_cursor: null` on the last page.
 *
 * The cursor is an opaque base64 string encoding the KEY of the last item on
 * the previous page (keyset, not offset — inserting rows before the cursor
 * never shifts subsequent pages). The server caps `limit` at {@link MAX_LIMIT}
 * silently.
 */

export interface CursorPage<T> {
  items: T[];
  next_cursor: string | null;
}

export const MAX_LIMIT = 200;
export const DEFAULT_LIMIT = 50;

/** Thrown for malformed or stale (unknown-key) cursors. */
export class CursorError extends Error {
  override name = "CursorError";
}

/**
 * Encode the keyset key of the last item of a page as an opaque cursor.
 * Canonical `Cursor` is base64url (`^[A-Za-z0-9_-]+$`, unpadded) — plain
 * padded base64 (`+`, `/`, `=`) is invalid on the wire.
 */
export function encodeCursor(lastKey: string): string {
  // btoa is available in Node >= 16 and every browser.
  return btoa(JSON.stringify({ k: lastKey }))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Decode an opaque cursor back to the keyset key it wraps. */
export function decodeCursor(cursor: string): string {
  let parsed: unknown;
  try {
    const base64 = cursor.replace(/-/g, "+").replace(/_/g, "/");
    parsed = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")));
  } catch {
    throw new CursorError(`malformed cursor: ${JSON.stringify(cursor)}`);
  }
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    typeof (parsed as { k?: unknown }).k !== "string"
  ) {
    throw new CursorError(`malformed cursor payload: ${JSON.stringify(cursor)}`);
  }
  return (parsed as { k: string }).k;
}

/**
 * Clamp a requested limit into [1, MAX_LIMIT]. Missing / non-finite /
 * non-positive values fall back to {@link DEFAULT_LIMIT}; oversized values are
 * capped at {@link MAX_LIMIT} silently (convention: no error).
 */
export function clampLimit(limit?: number | null): number {
  if (limit == null || !Number.isFinite(limit) || limit <= 0) return DEFAULT_LIMIT;
  return Math.min(Math.floor(limit), MAX_LIMIT);
}

/**
 * Paginate an already-filtered, already-sorted array with a keyset cursor.
 *
 * @param items  full result set in server sort order
 * @param key    stable unique key per item (usually the id) — the keyset
 * @param opts   cursor from the previous page's `next_cursor`, and limit
 * @throws CursorError when the cursor is malformed or its key no longer exists
 */
export function paginate<T>(
  items: readonly T[],
  key: (item: T) => string,
  opts: { cursor?: string | null; limit?: number | null } = {},
): CursorPage<T> {
  const limit = clampLimit(opts.limit);
  let start = 0;
  if (opts.cursor) {
    const lastKey = decodeCursor(opts.cursor);
    const idx = items.findIndex((item) => key(item) === lastKey);
    if (idx === -1) {
      throw new CursorError(`cursor references an unknown key`);
    }
    start = idx + 1;
  }
  const page = items.slice(start, start + limit);
  const hasMore = start + page.length < items.length;
  const last = page[page.length - 1];
  return {
    items: page,
    next_cursor: hasMore && last !== undefined ? encodeCursor(key(last)) : null,
  };
}
